import React, { useEffect, useState } from 'react'
import { apiClient } from '../services/api'
import { Article } from '../types'

interface Props {
  onSaved: (article: Article) => void
  initialUrl?: string
}

export default function SaveArticleForm({ onSaved, initialUrl }: Props) {
  // Start empty; the effect below syncs initialUrl on mount and when it changes
  // (e.g. when the app receives a Web Share Target redirect).
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setUrl(initialUrl || '')
  }, [initialUrl])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let trimmed = url.trim()
    if (!trimmed) return
    if (!/^https?:\/\//i.test(trimmed)) trimmed = 'https://' + trimmed

    setLoading(true)
    setError(null)
    try {
      const article = await apiClient.createArticle({ url: trimmed })
      onSaved(article)
      setUrl('')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save article')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className="save-form" onSubmit={handleSubmit}>
      <div className="save-form-row">
        <label htmlFor="save-url-input" className="sr-only">
          Article URL
        </label>
        <input
          id="save-url-input"
          name="url"
          type="url"
          inputMode="url"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="Type a URL to save…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          disabled={loading}
          required
        />
        <button type="submit" className="btn-primary" disabled={loading || !url.trim()}>
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
      {error && (
        <div className="save-form-error" role="alert">
          {error}
        </div>
      )}
    </form>
  )
}
